import styled, { keyframes, createGlobalStyle } from 'styled-components';

// Main Container
export const WelcomeContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: var(--bg-primary);
  position: relative;
  overflow-x: hidden;
`;

// Header/Navigation
export const Header = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: var(--bg-primary);
  padding: 1rem 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  box-shadow: var(--shadow-sm);
  border-bottom: 1px solid var(--card-border);
`;

export const HeaderContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 4rem;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 968px) {
    padding: 0 2rem;
  }

  @media (max-width: 768px) {
    padding: 0 1.5rem;
    flex-wrap: wrap;
  }

  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

export const Logo = styled.div`
  display: flex;
  align-items: center;
  font-weight: 900;
  letter-spacing: -2px;
  font-size: 2.5rem;
  cursor: pointer;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
    letter-spacing: -1px;
  }
`;

export const LogoSJ = styled.span`
  color: var(--accent-orange);
  font-weight: 900;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -2px;

  @media (max-width: 480px) {
    letter-spacing: -1px;
  }
`;

export const LogoMate = styled.span`
  color: var(--text-gray);
  font-weight: 900;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -2px;

  @media (max-width: 480px) {
    letter-spacing: -1px;
  }
`;

export const NavLinks = styled.nav`
  display: flex;
  gap: 2rem;
  align-items: center;
  margin-left: auto;
  margin-right: 2rem;

  @media (max-width: 968px) {
    gap: 1.5rem;
    margin-right: 1.5rem;
  }

  @media (max-width: 768px) {
    gap: 1rem;
    margin-right: 1rem;
    display: none;
  }
`;

export const NavLink = styled.button`
  background: none;
  border: none;
  color: var(--text-gray);
  font-size: 0.85rem;
  font-weight: 800;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  transition: var(--transition-normal);
  padding: 0.25rem 0;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  outline: none !important;
  box-shadow: none !important;

  &:hover {
    color: var(--accent-orange);
  }

  &:focus,
  &:focus-visible,
  &:active {
    outline: none !important;
    box-shadow: none !important;
    border: none !important;
  }

  @media (max-width: 968px) {
    font-size: 0.8rem;
  }
`;

export const NavHomeIcon = styled.button`
  background: none;
  border: none;
  color: var(--text-gray);
  cursor: pointer;
  transition: var(--transition-normal);
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  outline: none !important;
  box-shadow: none !important;

  &:hover {
    color: var(--accent-orange);
  }

  &:focus,
  &:focus-visible,
  &:active {
    outline: none !important;
    box-shadow: none !important;
    border: none !important;
  }

  @media (max-width: 968px) {
    font-size: 1rem;
  }
`;

export const NavButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
    flex-wrap: wrap;
  }
`;

export const LogInButton = styled.button`
  padding: var(--button-padding, 0.4rem 0.75rem);
  font-size: var(--button-font-size, 0.6rem);
  white-space: nowrap;
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;
  border: 2px solid var(--accent-orange);
  border-radius: var(--radius-md);
  cursor: pointer;
  background: transparent;
  color: var(--accent-orange);
  font-weight: 700;
  letter-spacing: 0.1em;
  transition: var(--transition-normal);

  &:hover {
    background: var(--accent-orange);
    color: var(--text-light);
    transform: translateY(-2px);
    box-shadow: var(--shadow-hover);
  }
`;

export const PrimaryButton = styled.button`
  padding: 0.4rem 1rem;
  font-size: 0.75rem;
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;
  border: 2px solid var(--accent-orange);
  border-radius: 0.5rem;
  cursor: pointer;
  background: var(--accent-orange);
  color: var(--text-light);
  font-weight: 800;
  letter-spacing: -0.02em;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-sizing: border-box;

  &:hover {
    background: var(--accent-orange-dark);
    border-color: var(--accent-orange-dark);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 480px) {
    padding: 0.4rem 1rem;
    font-size: 0.75rem;
  }
`;

export const SignInButton = styled(PrimaryButton)`
  background: var(--text-light);
  color: var(--accent-orange);
  border: 2px solid var(--accent-orange);

  &:hover {
    background: var(--accent-orange);
    color: var(--text-light);
    border-color: var(--accent-orange);
  }
`;

export const DashboardButton = styled.button`
  padding: var(--button-padding, 0.4rem 0.75rem);
  font-size: var(--button-font-size, 0.6rem);
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;
  border: 2px solid transparent;
  border-radius: var(--radius-md);
  cursor: pointer;
  background: var(--accent-orange);
  color: var(--text-light);
  font-weight: 800;
  letter-spacing: -0.02em;
  transition: var(--transition-normal);
  box-shadow: var(--shadow-md);
  white-space: nowrap;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: var(--accent-orange-dark);
    transform: translateY(-2px);
    box-shadow: var(--shadow-hover);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 480px) {
    padding: 0.35rem 0.65rem;
    font-size: var(--button-font-size, 0.6rem);
  }
`;

export const DashboardBadge = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 0.75rem;
  color: var(--text-light);
  font-family: 'Poppins', sans-serif;
`;

export const SecondaryButton = styled.button`
  padding: 0.4rem 1rem;
  font-size: 0.75rem;
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;
  border: 2px solid var(--accent-orange);
  border-radius: 0.5rem;
  cursor: pointer;
  background: transparent;
  color: var(--accent-orange);
  font-weight: 800;
  letter-spacing: -0.02em;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-sizing: border-box;

  &:hover {
    background: var(--accent-orange);
    color: var(--text-light);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 0.4rem 1rem;
    font-size: 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 0.4rem 1rem;
    font-size: 0.75rem;
  }
`;

// Section Containers
export const Section = styled.section`
  width: 100%;
  padding: 6rem 2rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 4rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 3rem 1rem;
  }
`;

export const HeroSectionContainer = styled.section`
  width: 100%;
  padding: 8rem 4rem 4rem;
  max-width: 1400px;
  margin: 0 auto;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: 968px) {
    padding: 5rem 2rem 3rem;
  }

  @media (max-width: 768px) {
    padding: 4.5rem 1.5rem 3rem;
  }

  @media (max-width: 480px) {
    padding: 4rem 1rem 2rem;
  }
`;

export const SectionTitle = styled.h2`
  font-size: clamp(1.75rem, 5vw, 2.5rem);
  font-weight: 900;
  color: var(--text-dark);
  text-align: center;
  margin-bottom: 1.5rem;
  font-family: 'Poppins', sans-serif;
  line-height: 1.2;

  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
`;

export const SectionSubtitle = styled.p`
  font-size: clamp(0.9rem, 2vw, 1.2rem);
  color: var(--text-muted);
  text-align: center;
  max-width: 800px;
  margin: 0 auto 4rem;
  line-height: 1.7;
  font-family: 'Poppins', sans-serif;

  @media (max-width: 768px) {
    margin: 0 auto 3rem;
    font-size: clamp(0.85rem, 1.8vw, 1.1rem);
  }

  @media (max-width: 480px) {
    margin: 0 auto 2rem;
    font-size: clamp(0.8rem, 1.5vw, 1rem);
    line-height: 1.6;
  }
`;

// Hero Section
export const HeroSection = styled(HeroSectionContainer)`
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 1.5rem;
  align-items: center;
  overflow-x: hidden;
  max-width: 100%;
  box-sizing: border-box;
  width: 100%;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 0.9fr;
    gap: 2rem;
  }

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
    text-align: center;
    min-height: auto;
  }

  @media (max-width: 768px) {
    gap: 2.5rem;
  }

  @media (max-width: 480px) {
    gap: 2rem;
  }
`;

export const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  max-width: 100%;
  box-sizing: border-box;
  overflow-wrap: break-word;
  word-wrap: break-word;
  width: 100%;
  padding-right: 1rem;

  @media (max-width: 968px) {
    align-items: center;
    padding-right: 0;
  }
`;

export const HeroTitle = styled.h1`
  font-size: clamp(2.25rem, 5.5vw, 3.25rem);
  font-weight: 900;
  color: var(--text-dark);
  margin-bottom: 1.5rem;
  margin-left: 2rem;
  line-height: 1.2;
  font-family: 'Poppins', sans-serif;
  text-align: left;
  width: 100%;
  max-width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15), 0 0 1px rgba(0, 0, 0, 0.2);
  letter-spacing: -0.02em;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    font-size: clamp(2.1rem, 5.3vw, 3.1rem);
  }

  @media (max-width: 968px) {
    text-align: center;
    font-size: clamp(2.25rem, 5.5vw, 3rem);
    margin-left: 0;
    padding: 0 1rem;
    margin-top: 2.75rem;
  }

  @media (max-width: 768px) {
    font-size: clamp(2rem, 5.2vw, 2.75rem);
  }

  @media (max-width: 480px) {
    font-size: clamp(2rem, 6vw, 2.5rem);
    margin-left: 0;
    padding: 0 0.5rem;
  }
`;

export const HeroSubtitle = styled.p`
  font-size: clamp(0.85rem, 1.3vw, 1rem);
  color: var(--text-muted);
  margin-bottom: 3.5rem;
  margin-left: 2rem;
  line-height: 1.7;
  font-family: 'Poppins', sans-serif;
  text-align: left;
  width: 100%;
  max-width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    font-size: clamp(0.82rem, 1.25vw, 0.98rem);
  }

  @media (max-width: 968px) {
    text-align: center;
    font-size: clamp(0.8rem, 1.2vw, 0.95rem);
    margin-bottom: 3rem;
    margin-left: 0;
    padding: 0 1rem;
  }

  @media (max-width: 768px) {
    font-size: clamp(0.78rem, 1.15vw, 0.92rem);
    margin-bottom: 2.75rem;
  }

  @media (max-width: 480px) {
    font-size: clamp(0.75rem, 1.1vw, 0.9rem);
    margin-bottom: 2.5rem;
    margin-left: 0;
    padding: 0 0.5rem;
  }
`;

export const HeroButtons = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: flex-start;
  flex-wrap: wrap;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  margin-top: 1rem;
  margin-left: 2rem;

  /* Make hero CTAs larger than default buttons */
  ${PrimaryButton}, ${SecondaryButton} {
    padding: 0.8rem 1.75rem;
    font-size: 0.95rem;
  }

  @media (max-width: 968px) {
    justify-content: center;
    margin-left: 0;
    padding: 0 1rem;
  }

  @media (max-width: 480px) {
    padding: 0 0.5rem;
  }

  @media (max-width: 968px) {
    justify-content: center;
    margin-top: 0.75rem;
    margin-left: 0;
    
    ${PrimaryButton}, ${SecondaryButton} {
      padding: 0.75rem 1.5rem;
      font-size: 0.9rem;
    }
  }

  @media (max-width: 480px) {
    gap: 1rem;
    flex-direction: column;
    width: 100%;
    margin-top: 0.5rem;
    
    button {
      width: 100%;
      max-width: 100%;
      padding: 0.85rem 1.5rem;
      font-size: 1rem;
    }
  }
`;

export const HeroVisual = styled.div`
  width: 100%;
  height: 350px;
  background: var(--bg-primary);
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--card-border);
  aspect-ratio: 16 / 9;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 968px) {
    height: 300px;
  }

  @media (max-width: 768px) {
    height: 250px;
  }

  @media (max-width: 480px) {
    height: 200px;
  }
`;

export const PlaceholderText = styled.p`
  color: var(--text-muted);
  font-size: 1.1rem;
  font-family: 'Poppins', sans-serif;
`;

export const HeroGraphContainer = styled.div`
  width: 100%;
  max-width: 200%;
  margin: 0;
  overflow: hidden;
  /* Extend to the edge of the screen: cancel section's right padding so image reaches the edge */
  margin-right: -4rem;
  width: calc(100% + 4rem);

  @media (max-width: 968px) {
    margin-right: -2rem;
    width: calc(100% + 2rem);
  }

  @media (max-width: 768px) {
    margin-right: 0;
    width: 100%;
  }

  @media (max-width: 480px) {
    margin-right: 0;
    width: 100%;
  }
`;

export const HeroImageWrapper = styled.div`
  width: 100%;
  line-height: 0;
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;

  @media (max-width: 768px) {
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }

  /* Same big height – image size unchanged */
  @media (min-width: 769px) {
    height: 520px;
  }

  @media (min-width: 1024px) {
    height: 560px;
  }

  @media (max-width: 768px) {
    height: auto;
    min-height: 340px;
  }

  @media (max-width: 480px) {
    min-height: 10px;
  }
`;

export const HeroImage = styled.img`
  display: block;
  vertical-align: middle;
  /* max-width/max-height set via JS (natural dimensions) so we never upscale; positioning unchanged */

  /* Desktop: same size and position as before */
  @media (min-width: 769px) {
    width: auto;
    min-width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: 32% center;
  }

  /* Mobile: same as before */
  @media (max-width: 768px) {
    width: 120%;
    height: auto;
    min-height: 340px;
    object-fit: contain;
    object-position: center center;
  }

  @media (max-width: 480px) {
    min-height: 200px;
  }
`;

// Tagline section (below Hero): "Stop losing your data in Spreadsheets. There is a better way."
export const TaglineSection = styled.section`
  width: 100%;
  padding: 4rem 2rem;
  max-width: 900px;
  margin: 4rem auto;
  text-align: center;

  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
    margin: 3rem auto;
  }

  @media (max-width: 480px) {
    padding: 2.5rem 1rem;
    margin: 2.5rem auto;
  }
`;

export const TaglineTitle = styled.h2`
  font-size: clamp(2.6rem, 4.5vw, 2.35rem);
  font-weight: 800;
  color: var(--text-dark);
  margin: 0 0 0.5rem;
  font-family: 'Poppins', sans-serif;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: clamp(1.75rem, 5vw, 2.5rem);
    font-weight: 900;
  }

  @media (max-width: 480px) {
    font-size: clamp(1.75rem, 5vw, 2.5rem);
    font-weight: 900;
  }
`;

export const TaglineSubline = styled.p`
  font-size: clamp(0.85rem, 1.6vw, 1.05rem);
  font-weight: 600;
  color: var(--accent-orange);
  margin: 0;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: clamp(1rem, 3vw, 1.2rem);
    font-weight: 700;
  }

  @media (max-width: 480px) {
    font-size: clamp(0.95rem, 2.8vw, 1.15rem);
    font-weight: 700;
  }
`;

export const GraphTabContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid var(--card-border);
  padding-bottom: 0.5rem;
  justify-content: center;
`;

export const GraphTabButton = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
  font-weight: 800;
  font-family: 'Poppins', sans-serif;
  color: ${props => props.$active ? 'var(--accent-orange)' : 'var(--text-muted)'};
  cursor: pointer;
  transition: var(--transition-normal);
  text-transform: uppercase;
  letter-spacing: -0.02em;
  position: relative;
  outline: none;

  &:hover {
    color: var(--accent-orange);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    right: 0;
    height: 2px;
    background: var(--accent-orange);
    opacity: ${props => props.$active ? 1 : 0};
    transition: var(--transition-normal);
  }
`;

export const GraphChartWrapper = styled.div`
  flex: 1;
  min-height: 300px;
  width: 100%;
  padding-bottom: 0.5rem;
`;

// Three Column Grid
export const ThreeColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.5rem;
  margin-top: 4rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`;

export const FeatureCard = styled.div`
  background: var(--bg-primary);
  border: 2px solid var(--card-border);
  border-radius: var(--radius-lg);
  padding: 3rem 2.5rem;
  text-align: left;
  transition: var(--transition-normal);
  box-shadow: var(--shadow-md);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--accent-orange) 0%, transparent 100%);
    opacity: 0;
    transition: var(--transition-normal);
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: var(--shadow-lg);
    border-color: var(--accent-orange);
    
    &::before {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

export const FeatureCardTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--accent-orange);
  margin-bottom: 1.5rem;
  font-family: 'Poppins', sans-serif;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 768px) {
    font-size: 1.15rem;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1.05rem;
  }
`;

export const FeatureCardIcon = styled.span`
  font-size: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--accent-orange);

  svg {
    width: 100%;
    height: 100%;
  }

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

export const FeatureCardList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
`;

export const FeatureCardButtonWrapper = styled.div`
  margin-top: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const FeatureCardItem = styled.li`
  padding: 0.4rem 0;
  color: var(--text-gray);
  font-size: 0.85rem;
  line-height: 1.5;
  font-family: 'Poppins', sans-serif;
  position: relative;
  padding-left: 1.5rem;
  font-weight: 700;

  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: var(--accent-orange);
    font-weight: 700;
  }

  @media (max-width: 768px) {
    font-size: 0.85rem;
    padding: 0.4rem 0;
    padding-left: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

// Features Grid
export const FeaturesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4rem;
  margin-top: 4rem;
`;

export const FeatureSection = styled.div<{ $reverse?: boolean }>`
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 3rem;
  align-items: center;
  padding: 2rem 0;

  ${props => props.$reverse && `
    direction: rtl;
    
    > * {
      direction: ltr;
    }
  `}

  @media (max-width: 1024px) {
    gap: 2.5rem;
    padding: 1.75rem 0;
  }

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: 1.5rem 0;
    direction: ltr;
    
    > * {
      direction: ltr;
    }
  }

  @media (max-width: 768px) {
    gap: 1.25rem;
    padding: 1.25rem 0;
  }

  @media (max-width: 480px) {
    gap: 1rem;
    padding: 1rem 0;
  }
`;

export const FeatureContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 968px) {
    gap: 0.75rem;
    margin-bottom: 0.5rem;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
    margin-bottom: 0.25rem;
  }
`;

export const FeatureIcon = styled.div`
  font-size: 2.5rem;
  color: var(--accent-orange);
  margin-bottom: 1rem;
`;

export const FeatureTitle = styled.h4`
  font-size: 2rem;
  font-weight: 900;
  color: var(--text-dark);
  margin-bottom: 0.75rem;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -0.02em;
  line-height: 1.2;
  text-align: center;

  @media (max-width: 1024px) {
    font-size: 1.9rem;
  }

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }
`;

export const FeatureDescription = styled.p`
  color: var(--text-muted);
  font-size: clamp(0.9rem, 1.2vw, 1rem);
  line-height: 1.7;
  font-family: 'Poppins', sans-serif;

  @media (max-width: 1024px) {
    font-size: clamp(0.88rem, 1.15vw, 0.98rem);
  }

  @media (max-width: 768px) {
    font-size: clamp(0.85rem, 1.1vw, 0.95rem);
    line-height: 1.6;
  }

  @media (max-width: 480px) {
    font-size: clamp(0.8rem, 1vw, 0.9rem);
    line-height: 1.5;
  }
`;

export const FeatureImageFrame = styled.div`
  width: 100%;
  min-height: 300px;
  background: var(--bg-primary);
  border-radius: var(--radius-lg);
  border: 2px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--shadow-md);
  overflow: hidden;
  position: relative;
  aspect-ratio: 16 / 10;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 150, 0, 0.05) 0%, rgba(255, 150, 0, 0.02) 100%);
    z-index: 0;
  }

  @media (max-width: 1024px) {
    min-height: 280px;
  }

  @media (max-width: 968px) {
    min-height: 260px;
  }

  @media (max-width: 768px) {
    min-height: 240px;
  }

  @media (max-width: 480px) {
    min-height: 200px;
  }
`;

export const FeatureImagePlaceholder = styled.div`
  color: var(--text-muted);
  font-size: 0.9rem;
  font-family: 'Poppins', sans-serif;
  text-align: center;
  z-index: 1;
  position: relative;
`;

export const FeatureImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
  object-position: center;
  display: block;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
`;

// How It Works
export const StepsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.5rem;
  margin-top: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

export const StepCard = styled.div`
  text-align: center;
  padding: 2.5rem;
  background: linear-gradient(135deg, var(--accent-orange) 0%, #e68900 100%);
  border: none;
  border-radius: var(--radius-lg);
  transition: var(--transition-normal);
  color: var(--text-light);

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-lg);
  }
`;

export const StepNumber = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  color: var(--text-light);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 900;
  font-size: 1.5rem;

  svg {
    width: 2rem;
    height: 2rem;
    color: var(--text-light);
  }
`;

export const StepTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--text-light);
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
  }
`;

export const StepDescription = styled.p`
  color: rgba(255, 255, 255, 0.95);
  font-size: 1rem;
  line-height: 1.6;
  font-family: 'Poppins', sans-serif;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

// Pricing
export const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 2rem;
  margin-top: 4rem;
  width: 100%;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
  }

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

export const PricingCard = styled.div<{ featured?: boolean }>`
  background: var(--card-bg);
  border: 2px solid ${props => props.featured ? 'var(--accent-orange)' : 'var(--card-border)'};
  border-radius: var(--radius-lg);
  padding: 3rem 2.5rem;
  text-align: center;
  position: relative;
  transition: var(--transition-normal);
  box-shadow: ${props => props.featured ? 'var(--shadow-lg)' : 'var(--shadow-sm)'};
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-lg);
  }
`;

export const PricingBadge = styled.div`
  position: absolute;
  top: -12px;
  left: -4px;
  background: var(--accent-orange);
  color: var(--text-light);
  padding: 0.3rem 0.6rem;
  border-radius: var(--radius-full);
  font-size: 0.5rem;
  font-weight: 700;
  text-transform: uppercase;
  font-family: 'Poppins', sans-serif;
  z-index: 10;
  white-space: normal;
  line-height: 1.2;
  text-align: center;
  max-width: 60px;
`;

export const PricingDiscountBadgeTop = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--accent-orange);
  color: var(--text-light);
  padding: 0.65rem 1.25rem;
  border-radius: var(--radius-full);
  font-size: 0.9rem;
  font-weight: 700;
  text-transform: uppercase;
  font-family: 'Poppins', sans-serif;
  z-index: 10;
  white-space: nowrap;
`;

export const PricingTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: 0.5rem;
  font-family: 'Poppins', sans-serif;
  min-height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    font-size: 1.3rem;
    min-height: 2.2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.2rem;
    min-height: 2rem;
  }
`;

export const PricingPrice = styled.div`
  font-size: 2.5rem;
  font-weight: 900;
  color: var(--accent-orange);
  margin: 1rem 0;
  font-family: 'Poppins', sans-serif;
  min-height: 4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 2.2rem;
    min-height: 3.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
    margin: 0.75rem 0;
    min-height: 3rem;
  }
`;

export const PricingPeriod = styled.span<{ strikethrough?: boolean }>`
  font-size: 1rem;
  color: var(--text-muted);
  font-weight: 700;
  ${props => props.strikethrough && 'text-decoration: line-through;'}
`;

export const PricingFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1.5rem 0;
  text-align: left;
  flex-grow: 1;
`;

export const PricingFeature = styled.li<{ unavailable?: boolean }>`
  padding: 0.4rem 0;
  color: var(--text-gray);
  font-size: 0.85rem;
  line-height: 1.5;
  font-family: 'Poppins', sans-serif;
  position: relative;
  padding-left: 1.5rem;
  font-weight: 700;
  opacity: ${props => props.unavailable ? 0.5 : 1};

  &:before {
    content: '${props => props.unavailable ? '✗' : '✓'}';
    position: absolute;
    left: 0;
    color: ${props => props.unavailable ? 'var(--text-muted)' : 'var(--accent-orange)'};
    font-weight: 700;
  }
`;

export const PricingNote = styled.p`
  font-size: 0.9rem;
  color: var(--text-muted);
  margin-top: 1rem;
  font-style: italic;
  font-family: 'Poppins', sans-serif;
`;

export const PricingDiscountContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  margin: 1rem 0;
  min-height: 4rem;
  justify-content: center;

  @media (max-width: 768px) {
    min-height: 3.5rem;
  }

  @media (max-width: 480px) {
    min-height: 3rem;
  }
`;

export const PricingEmptySpacer = styled.div`
  min-height: 4rem;
  margin: 1rem 0;

  @media (max-width: 768px) {
    min-height: 3.5rem;
  }

  @media (max-width: 480px) {
    min-height: 3rem;
  }
`;

export const PricingOriginalPrice = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-muted);
  text-decoration: line-through;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -0.02em;
`;

export const PricingDiscountPrice = styled.div`
  font-size: 2.5rem;
  font-weight: 900;
  color: var(--accent-orange);
  font-family: 'Poppins', sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0;
  letter-spacing: -0.03em;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
    flex-direction: column;
    gap: 0;
  }
`;

export const PricingDiscountBadge = styled.span`
  display: inline-block;
  background: var(--accent-orange);
  color: var(--text-light);
  padding: 0.25rem 0.75rem;
  border-radius: var(--radius-sm);
  font-size: 0.85rem;
  font-weight: 700;
  margin-left: 0.5rem;
  font-family: 'Poppins', sans-serif;
  white-space: nowrap;
`;

// FAQ
export const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  margin-top: 4rem;
`;

export const FAQItem = styled.div`
  margin-bottom: 1.5rem;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius-md);
  overflow: hidden;
  transition: var(--transition-normal);

  &:hover {
    border-color: var(--accent-orange);
    box-shadow: var(--shadow-md);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

export const FAQQuestion = styled.div`
  padding: 2rem;
  font-weight: 700;
  color: var(--text-dark);
  font-size: 1.1rem;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    color: var(--accent-orange);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    font-size: 0.95rem;
  }
`;

export const FAQAnswer = styled.div`
  padding: 0 2rem 2rem;
  color: var(--text-muted);
  font-size: 1rem;
  line-height: 1.7;
  font-family: 'Poppins', sans-serif;

  @media (max-width: 768px) {
    padding: 0 1.5rem 1.5rem;
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    padding: 0 1rem 1rem;
    font-size: 0.9rem;
  }
`;

// Testimonials
export const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2.5rem;
  margin-top: 4rem;
`;

export const TestimonialCard = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius-lg);
  padding: 2.5rem;
  text-align: left;
  transition: var(--transition-normal);

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-lg);
    border-color: var(--accent-orange);
  }
`;

export const TestimonialText = styled.p`
  color: var(--text-gray);
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-style: italic;
  font-family: 'Poppins', sans-serif;
`;

export const TestimonialAuthor = styled.div`
  font-weight: 700;
  color: var(--text-dark);
  font-family: 'Poppins', sans-serif;
`;

export const TestimonialRole = styled.div`
  font-size: 0.9rem;
  color: var(--text-muted);
  font-family: 'Poppins', sans-serif;
`;

// Stats
export const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.5rem;
  margin-top: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

export const StatCard = styled.div`
  text-align: center;
  padding: 2.5rem;
  background: var(--bg-primary);
  border-radius: var(--radius-lg);
`;

export const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: 900;
  color: var(--accent-orange);
  margin-bottom: 0.5rem;
  font-family: 'Poppins', sans-serif;
`;

export const StatLabel = styled.div`
  font-size: 1.1rem;
  color: var(--text-gray);
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
`;

// Footer
export const Footer = styled.footer`
  background: var(--bg-primary);
  padding: 4rem 2rem 2rem;
  margin-top: 5rem;
  border-top: 4px solid var(--accent-orange);

  @media (max-width: 768px) {
    padding: 3rem 1.5rem 1.5rem;
    margin-top: 4rem;
  }

  @media (max-width: 480px) {
    padding: 2rem 1rem 1rem;
    margin-top: 3rem;
  }
`;

export const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 3rem;
  margin-bottom: 2.5rem;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`;

export const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  align-items: flex-start;

  @media (max-width: 768px) {
    align-items: center;
    text-align: center;
  }
`;

export const FooterLogo = styled.div`
  display: flex;
  align-items: center;
  font-weight: 900;
  letter-spacing: -2px;
  font-size: 2rem;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
    letter-spacing: -1px;
  }
`;

export const FooterLogoSJ = styled.span`
  color: var(--accent-orange);
  font-weight: 900;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -2px;

  @media (max-width: 480px) {
    letter-spacing: -1px;
  }
`;

export const FooterLogoMate = styled.span`
  color: var(--text-gray);
  font-weight: 900;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -2px;

  @media (max-width: 480px) {
    letter-spacing: -1px;
  }
`;

export const FooterTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 800;
  color: var(--text-dark);
  margin-bottom: 0.5rem;
  margin-top: 0;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -0.02em;
`;

export const FooterLink = styled.a`
  color: var(--text-muted);
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 600;
  transition: var(--transition-normal);
  font-family: 'Poppins', sans-serif;
  letter-spacing: -0.01em;
  margin-bottom: 0.15rem;
  display: block;
  line-height: 1.4;

  &:hover {
    color: var(--accent-orange);
  }
`;

export const FooterBottom = styled.div`
  border-top: 1px solid var(--card-border);
  padding-top: 2rem;
  text-align: center;
  color: var(--text-muted);
  font-size: 0.9rem;
  font-family: 'Poppins', sans-serif;
  max-width: 1200px;
  margin: 0 auto;
`;

export const FooterLine = styled.div`
  width: 100vw;
  height: 4px;
  background: var(--accent-orange);
  margin-top: 2rem;
`;

export const FooterText = styled.div`
  color: var(--text-gray);
  font-size: 0.9rem;
  margin: 0.5rem 0;
  text-align: center;
  letter-spacing: -0.01em;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
`;

export const FooterSocialLinks = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  margin-top: 0.25rem;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

export const FooterSocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: auto;
  height: auto;
  border-radius: 0;
  background: transparent;
  color: var(--text-dark);
  transition: var(--transition-normal);
  font-size: 1.2rem;
  padding: 0;

  svg {
    width: 20px;
    height: 20px;
    color: var(--text-dark);
  }

  &:hover {
    background: transparent;
    color: var(--accent-orange);
    
    svg {
      color: var(--accent-orange);
    }
  }
`;

// CTA Section
export const CTASection = styled(Section)`
  background: linear-gradient(135deg, var(--accent-orange) 0%, var(--accent-orange-dark) 100%);
  color: var(--text-light);
  border-radius: var(--radius-lg);
  margin: 8rem auto;
  max-width: 1000px;
  padding: 6rem 2rem;
  text-align: center;

  @media (max-width: 768px) {
    padding: 5rem 1.5rem;
    margin: 6rem auto;
  }

  @media (max-width: 480px) {
    padding: 4rem 1rem;
    margin: 4rem auto;
  }
`;

export const CTATitle = styled.h2`
  font-size: clamp(1.5rem, 4vw, 2.5rem);
  font-weight: 900;
  color: var(--text-light);
  margin-bottom: 1.5rem;
  font-family: 'Poppins', sans-serif;
  line-height: 1.2;

  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
`;

export const CTASubtitle = styled.p`
  font-size: clamp(0.9rem, 1.8vw, 1.2rem);
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 2.5rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
  font-family: 'Poppins', sans-serif;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
    font-size: clamp(0.85rem, 1.6vw, 1.1rem);
  }

  @media (max-width: 480px) {
    margin-bottom: 1.5rem;
    font-size: clamp(0.8rem, 1.4vw, 1rem);
    line-height: 1.6;
  }
`;

export const CTAButton = styled(PrimaryButton)`
  background: var(--text-light);
  color: var(--accent-orange);
  border: none;
  font-size: 1.15rem;
  padding: 0.9rem 2.25rem;
  letter-spacing: -0.02em;

  &:hover {
    background: var(--bg-secondary);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    font-size: 1.05rem;
    padding: 0.8rem 1.9rem;
  }
`;

export const CTANote = styled.p`
  margin-top: 1.5rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  font-family: 'Poppins', sans-serif;
`;

// FREE Plan Section
export const FreePlanSection = styled(Section)`
  margin: 2.5rem auto 6rem;
  max-width: 1000px;
  padding: 3rem 3rem;

  @media (max-width: 768px) {
    padding: 2.5rem 2rem;
    margin: 2rem auto 4rem;
  }

  @media (max-width: 480px) {
    padding: 2rem 1.5rem;
    margin: 1.5rem auto 3rem;
  }
`;

export const FreePlanContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

export const FreePlanButtonWrapper = styled.div`
  margin-top: 2rem;
  text-align: center;
`;

export const FreePlanButton = styled(PrimaryButton)`
  padding: 0.85rem 2rem;
  font-size: 1.05rem;

  @media (max-width: 768px) {
    padding: 0.75rem 1.75rem;
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    padding: 0.7rem 1.5rem;
    font-size: 0.9rem;
  }
`;

export const FreePlanTitle = styled.h2`
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  font-weight: 900;
  color: var(--text-dark);
  text-align: center;
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;
  line-height: 1.2;

  @media (max-width: 480px) {
    margin-bottom: 0.75rem;
  }
`;

export const FreePlanSubtitle = styled.p`
  font-size: clamp(1rem, 1.8vw, 1.1rem);
  color: var(--text-muted);
  text-align: center;
  margin-bottom: 1.5rem;
  line-height: 1.7;
  font-family: 'Poppins', sans-serif;

  @media (max-width: 768px) {
    margin-bottom: 1.25rem;
    font-size: clamp(0.95rem, 1.6vw, 1.05rem);
  }

  @media (max-width: 480px) {
    margin-bottom: 1rem;
    font-size: clamp(0.9rem, 1.4vw, 1rem);
    line-height: 1.6;
  }
`;

export const FreePlanText = styled.div`
  font-size: clamp(1rem, 1.8vw, 1.1rem);
  color: var(--text-gray);
  line-height: 1.7;
  font-family: 'Poppins', sans-serif;
  text-align: center;
  max-width: 700px;
  margin: 0 auto;

  p {
    margin-bottom: 0;
    color: var(--text-gray);
  }

  strong {
    color: var(--accent-orange);
    font-weight: 700;
  }

  @media (max-width: 768px) {
    font-size: clamp(0.95rem, 1.6vw, 1.05rem);
    line-height: 1.6;
  }

  @media (max-width: 480px) {
    font-size: clamp(0.9rem, 1.4vw, 1rem);
  }

  @media (max-width: 480px) {
    font-size: clamp(0.6rem, 0.9vw, 0.7rem);
    line-height: 1.5;
  }
`;

// Problem/Solution Sections
export const ProblemSection = styled(Section)`
  background: var(--bg-primary);
`;

export const SolutionSection = styled(Section)`
  background: var(--bg-primary);
`;

// Security Section Styles
export const SecurityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2.5rem;
  margin-top: 3rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

export const SecurityItem = styled.div`
  text-align: center;
  padding: 2.5rem;
  background: var(--card-bg);
  border: 2px solid var(--card-border);
  border-radius: var(--radius-lg);
  transition: var(--transition-normal);

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-lg);
    border-color: var(--accent-orange);
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

export const SecurityIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 150, 0, 0.1);
  border-radius: 50%;
  color: var(--accent-orange);
  font-size: 2.5rem;

  svg {
    width: 100%;
    height: 100%;
  }

  @media (max-width: 768px) {
    width: 70px;
    height: 70px;
    font-size: 2rem;
  }
`;

export const SecurityTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

export const SecurityDescription = styled.p`
  color: var(--text-muted);
  font-size: 1rem;
  line-height: 1.6;
  font-family: 'Poppins', sans-serif;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

// Section Dividers - Minimalistic Orange Elements
export const SectionDivider = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem 0;
  position: relative;

  @media (max-width: 768px) {
    padding: 2rem 0;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 0;
  }
`;

export const DividerLine = styled.div`
  width: 80px;
  height: 3px;
  background: var(--accent-orange);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    background: var(--accent-orange);
    border-radius: 50%;
  }

  @media (max-width: 480px) {
    width: 60px;
    height: 2px;

    &::before {
      width: 6px;
      height: 6px;
    }
  }
`;

export const DividerAccent = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 0 1rem;
    gap: 0.75rem;
  }
`;

export const DividerSquare = styled.div`
  width: 6px;
  height: 6px;
  background: var(--accent-orange);
  transform: rotate(45deg);

  @media (max-width: 480px) {
    width: 5px;
    height: 5px;
  }
`;

export const DividerHorizontalLine = styled.div`
  flex: 1;
  max-width: 200px;
  height: 2px;
  background: linear-gradient(90deg, transparent 0%, var(--accent-orange) 50%, transparent 100%);

  @media (max-width: 480px) {
    max-width: 150px;
    height: 1.5px;
  }
`;

// Contact Form
export const ContactFormContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

export const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const ContactFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const ContactLabel = styled.label`
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-dark);
  font-family: 'Poppins', sans-serif;
`;

export const ContactInput = styled.input`
  padding: 0.75rem 1rem;
  border: 2px solid var(--card-border);
  border-radius: var(--radius-md);
  font-size: 1rem;
  font-family: 'Poppins', sans-serif;
  background: var(--card-bg);
  color: var(--text-dark);
  transition: var(--transition-normal);

  &:focus {
    outline: none;
    border-color: var(--accent-orange);
    box-shadow: 0 0 0 3px rgba(255, 150, 0, 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    padding: 0.65rem 0.85rem;
  }
`;

export const ContactTextarea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 2px solid var(--card-border);
  border-radius: var(--radius-md);
  font-size: 1rem;
  font-family: 'Poppins', sans-serif;
  background: var(--card-bg);
  color: var(--text-dark);
  resize: vertical;
  transition: var(--transition-normal);

  &:focus {
    outline: none;
    border-color: var(--accent-orange);
    box-shadow: 0 0 0 3px rgba(255, 150, 0, 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    padding: 0.65rem 0.85rem;
  }
`;

export const ContactSubmitButton = styled(PrimaryButton)`
  width: 100%;
  padding: 0.4rem 1rem;
  font-size: 0.75rem;
  margin-top: 0.5rem;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    padding: 0.4rem 1rem;
    font-size: 0.75rem;
  }
`;

export const ContactSuccessMessage = styled.div`
  padding: 1rem;
  background: rgba(34, 197, 94, 0.1);
  border: 2px solid rgba(34, 197, 94, 0.3);
  border-radius: var(--radius-md);
  color: #16a34a;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  text-align: center;
`;

export const ContactErrorMessage = styled.div`
  padding: 1rem;
  background: rgba(239, 68, 68, 0.1);
  border: 2px solid rgba(239, 68, 68, 0.3);
  border-radius: var(--radius-md);
  color: #dc2626;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  text-align: center;
`;

// Scroll Animations - Modern, Athletic, Energetic
const slideUpBounce = keyframes`
  0% {
    opacity: 0;
    transform: translateY(60px) scale(0.95);
  }
  60% {
    opacity: 0.8;
    transform: translateY(-8px) scale(1.02);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const slideInLeft = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-80px) rotate(-5deg);
  }
  100% {
    opacity: 1;
    transform: translateX(0) rotate(0deg);
  }
`;

const slideInRight = keyframes`
  0% {
    opacity: 0;
    transform: translateX(80px) rotate(5deg);
  }
  100% {
    opacity: 1;
    transform: translateX(0) rotate(0deg);
  }
`;

const scaleIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.8) rotateY(-15deg);
  }
  50% {
    transform: scale(1.05) rotateY(5deg);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotateY(0deg);
  }
`;

const fadeInZoom = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.9) translateZ(0);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateZ(0);
  }
`;

const staggerFadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(30px) scale(0.9);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(255, 150, 0, 0.4);
  }
  50% {
    box-shadow: 0 0 20px 10px rgba(255, 150, 0, 0.2);
  }
`;

// Global animation styles using createGlobalStyle
export const ScrollAnimateStyles = createGlobalStyle`
  /* Base animation styles - elements start completely hidden */
  .scroll-animate {
    opacity: 0 !important;
    will-change: transform, opacity;
    visibility: hidden;
    pointer-events: none;
  }

  /* Show content when animated in */
  .scroll-animate.animate-in {
    opacity: 1 !important;
    visibility: visible;
    pointer-events: auto;
  }

  /* Fallback: show content after 5 seconds ONLY if observer completely fails */
  @keyframes fadeInFallback {
    to {
      opacity: 1 !important;
      visibility: visible;
      pointer-events: auto;
    }
  }

  /* Apply fallback only if observer fails - very long delay to prevent any flash */
  .scroll-animate:not(.animate-in) {
    animation: fadeInFallback 0.1s ease 5s forwards;
  }

  /* Ensure content is visible if animations fail or on initial load */
  @media (prefers-reduced-motion: reduce) {
    .scroll-animate {
      opacity: 1 !important;
      animation: none !important;
      transform: none !important;
    }
  }

  /* Hero Section */
  ${HeroSection}.scroll-animate.animate-in {
    animation: ${fadeInZoom} 1s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }

  /* Feature Cards */
  ${FeatureCard}.scroll-animate {
    opacity: 0;
    transform: translateY(40px) scale(0.95);
  }

  ${FeatureCard}.scroll-animate.animate-in {
    animation: ${scaleIn} 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }

  ${FeatureCard}:nth-child(1).scroll-animate.animate-in {
    animation-delay: 0.1s;
  }

  ${FeatureCard}:nth-child(2).scroll-animate.animate-in {
    animation-delay: 0.2s;
  }

  ${FeatureCard}:nth-child(3).scroll-animate.animate-in {
    animation-delay: 0.3s;
  }

  /* Feature Sections */
  ${FeatureSection}.scroll-animate {
    opacity: 0;
  }

  ${FeatureSection}.scroll-animate.animate-in:nth-child(odd) {
    animation: ${slideInLeft} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }

  ${FeatureSection}.scroll-animate.animate-in:nth-child(even) {
    animation: ${slideInRight} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }

  /* Step Cards */
  ${StepCard}.scroll-animate {
    opacity: 0;
    transform: translateY(50px) scale(0.9);
  }

  ${StepCard}.scroll-animate.animate-in {
    animation: ${staggerFadeIn} 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }

  ${StepCard}:nth-child(1).scroll-animate.animate-in {
    animation-delay: 0.1s;
  }

  ${StepCard}:nth-child(2).scroll-animate.animate-in {
    animation-delay: 0.2s;
  }

  ${StepCard}:nth-child(3).scroll-animate.animate-in {
    animation-delay: 0.3s;
  }

  /* Pricing Cards */
  ${PricingCard}.scroll-animate {
    opacity: 0;
    transform: translateY(60px) scale(0.9) rotateX(10deg);
  }

  ${PricingCard}.scroll-animate.animate-in {
    animation: ${scaleIn} 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }

  ${PricingCard}:nth-child(1).scroll-animate.animate-in {
    animation-delay: 0.1s;
  }

  ${PricingCard}:nth-child(2).scroll-animate.animate-in {
    animation-delay: 0.2s;
  }

  ${PricingCard}:nth-child(3).scroll-animate.animate-in {
    animation-delay: 0.3s;
  }

  ${PricingCard}:nth-child(4).scroll-animate.animate-in {
    animation-delay: 0.4s;
  }


  /* Stat Cards */
  ${StatCard}.scroll-animate {
    opacity: 0;
    transform: scale(0.8);
  }

  ${StatCard}.scroll-animate.animate-in {
    animation: ${scaleIn} 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }

  ${StatCard}:nth-child(1).scroll-animate.animate-in {
    animation-delay: 0.1s;
  }

  ${StatCard}:nth-child(2).scroll-animate.animate-in {
    animation-delay: 0.2s;
  }

  ${StatCard}:nth-child(3).scroll-animate.animate-in {
    animation-delay: 0.3s;
  }

  /* FAQ Items */
  ${FAQItem}.scroll-animate {
    opacity: 0;
    transform: translateX(-30px);
  }

  ${FAQItem}.scroll-animate.animate-in {
    animation: ${slideInLeft} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }

  /* Contact Form */
  ${ContactForm}.scroll-animate {
    opacity: 0;
    transform: translateY(40px) scale(0.95);
  }

  ${ContactForm}.scroll-animate.animate-in {
    animation: ${slideUpBounce} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }

  /* Default section animation */
  .scroll-animate.animate-in:not(${HeroSection}):not(${FeatureCard}):not(${FeatureSection}):not(${StepCard}):not(${PricingCard}):not(${StatCard}):not(${FAQItem}):not(${ContactForm}) {
    animation: ${slideUpBounce} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }
`;
